import * as React from 'react';
import { cn } from '../../lib/utils';

/**
 * Table component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 216:2824):
 * - Header: grey-50 bg, grey-500 text, 12px uppercase
 * - Rows: white bg, grey-100 border
 * - Cells: 12px/16px padding
 * - Hover state: grey-50 bg
 */
const Table = React.forwardRef(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-sm', className)}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn('bg-grey-50 dark:bg-grey-900', className)}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t bg-grey-50 font-medium dark:bg-grey-900',
      className
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b border-grey-100 transition-colors',
      'hover:bg-grey-50 data-[state=selected]:bg-grey-100',
      'dark:border-grey-800 dark:hover:bg-grey-900/50 dark:data-[state=selected]:bg-grey-800',
      className
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'h-10 px-3 text-left align-middle font-medium',
      'text-xs uppercase tracking-wider text-grey-500',
      '[&:has([role=checkbox])]:pr-0',
      'dark:text-grey-400',
      className
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'px-3 py-3 align-middle text-grey-950',
      '[&:has([role=checkbox])]:pr-0',
      'dark:text-grey-50',
      className
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-grey-500 dark:text-grey-400', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
